import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "app/providers/ThemeProvider";
import "./RealEstateNavbar.css";

const ThemeToggle = lazy(() => import("shared/ui/ThemeToggle"));

const logoDark = "/images/LogoVectorizado.dark.png";
const logoLight = "/images/LogoVectorizado.light.png";

const SCROLL_THRESHOLD = 40;
const HAMBURGER_BREAKPOINT = 900;

const BASE = "/inmobiliaria";

const ROUTES = {
    home: BASE,
    nosotros: `${BASE}/nosotros`,
    compra: `${BASE}/compra`,
    venta: `${BASE}/venta`,
    renta: `${BASE}/renta`,
    contacto: `${BASE}/contacto`,
};

const NAV_ITEMS = [
    { label: "Inicio", to: ROUTES.home },
    { label: "Nosotros", to: ROUTES.nosotros },
    { label: "Compra", to: ROUTES.compra },
    { label: "Venta", to: ROUTES.venta },
    { label: "Renta", to: ROUTES.renta },
    { label: "Contáctanos", to: ROUTES.contacto },
];

function normalizePath(p) {
    const s = String(p || "");
    if (s.length > 1) return s.replace(/\/+$/, "");
    return s;
}

function isPathMatch(pathname, target) {
    if (!target) return false;
    const p = normalizePath(pathname).toLowerCase();
    const t = normalizePath(target).toLowerCase();
    if (p === t) return true;
    if (t === "/") return p === "/";
    return p.startsWith(`${t}/`);
}

function getScrollHost() {
    const container = document.querySelector(".App");
    return { container, host: container || window };
}

function scrollToTop(behavior = "smooth") {
    const { container } = getScrollHost();
    if (container) container.scrollTo({ top: 0, behavior });
    else window.scrollTo({ top: 0, behavior });
}

export default function Navbar() {
    const { darkMode } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    const navRef = useRef(null);
    const hamburgerRef = useRef(null);
    const closeRef = useRef(null);

    const [scrolled, setScrolled] = useState(() => {
        if (typeof window === "undefined") return false;
        const { container } = getScrollHost();
        const y = container ? container.scrollTop : window.scrollY;
        return y > SCROLL_THRESHOLD;
    });

    const [menuOpen, setMenuOpen] = useState(false);

    const logoSrc = useMemo(() => {
        if (!scrolled) return logoLight;
        return darkMode ? logoLight : logoDark;
    }, [scrolled, darkMode]);

    const activeLabel = useMemo(() => {
        const p = location.pathname || "/";
        if (isPathMatch(p, ROUTES.home)) return "Inicio";
        if (isPathMatch(p, ROUTES.nosotros)) return "Nosotros";
        if (isPathMatch(p, ROUTES.compra)) return "Compra";
        if (isPathMatch(p, ROUTES.venta)) return "Venta";
        if (isPathMatch(p, ROUTES.renta)) return "Renta";
        if (isPathMatch(p, ROUTES.contacto)) return "Contáctanos";
        return null;
    }, [location.pathname]);

    const isItemCurrent = useCallback(
        (item) => {
            const p = location.pathname || "/";
            return activeLabel === item.label || isPathMatch(p, item.to);
        },
        [activeLabel, location.pathname]
    );

    const closeMenu = useCallback(() => setMenuOpen(false), []);
    const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);

    const goTo = useCallback(
        (to) => {
            const current = normalizePath(location.pathname);
            const target = normalizePath(to);

            setMenuOpen(false);

            if (current === target) {
                scrollToTop("smooth");
                return;
            }

            navigate(to);
        },
        [location.pathname, navigate]
    );

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > HAMBURGER_BREAKPOINT && menuOpen) setMenuOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [menuOpen]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [menuOpen]);

    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (menuOpen) closeRef.current?.focus?.();
        else hamburgerRef.current?.focus?.();
    }, [menuOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    useEffect(() => {
        const { container, host } = getScrollHost();
        let raf = 0;

        const readY = () => (container ? container.scrollTop : window.scrollY);

        const apply = (y) => {
            const p = Math.max(0, Math.min(1, y / 260));
            if (navRef.current) navRef.current.style.setProperty("--re-scroll-p", String(p));
            const next = y > SCROLL_THRESHOLD;
            setScrolled((prev) => (prev === next ? prev : next));
        };

        const onScroll = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                apply(readY());
            });
        };

        host.addEventListener("scroll", onScroll, { passive: true });
        apply(readY());

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            host.removeEventListener("scroll", onScroll);
        };
    }, [location.pathname]);

    return (
        <>
            <nav
                ref={navRef}
                className={[
                    "re-nav",
                    darkMode ? "re-nav--dark" : "re-nav--light",
                    scrolled ? "re-nav--scrolled" : "re-nav--top",
                    menuOpen ? "re-nav--menu-open" : "",
                ].join(" ")}
                role="navigation"
                aria-label="Inmobiliaria Navigation"
            >
                <div className="re-nav__inner">
                    <div className="re-nav__left">
                        <button
                            type="button"
                            className="re-nav__brand"
                            aria-label="Inicio"
                            onClick={() => goTo(ROUTES.home)}
                        >
                            <img src={logoSrc} alt="Marca" className="re-nav__logo" loading="eager" decoding="sync" />
                        </button>
                    </div>

                    <div className="re-nav__center" aria-label="Primary">
                        <ul className="re-nav__list">
                            {NAV_ITEMS.map((item) => {
                                const current = isItemCurrent(item);
                                return (
                                    <li key={item.label} className={`re-nav__item ${current ? "is-current" : ""}`}>
                                        <button
                                            type="button"
                                            className="re-nav__link"
                                            aria-current={current ? "page" : undefined}
                                            onClick={() => goTo(item.to)}
                                        >
                                            <span className="re-nav__linkText">{item.label}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <div className="re-nav__right">
                        <div className="re-nav__toggle" aria-label="Theme toggle">
                            <Suspense fallback={<span className="re-toggle-skeleton" aria-hidden="true" />}>
                                <ThemeToggle />
                            </Suspense>
                        </div>

                        <button
                            ref={hamburgerRef}
                            type="button"
                            className={`re-nav__hamburger ${menuOpen ? "is-open" : ""}`}
                            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
                            aria-expanded={menuOpen}
                            onClick={toggleMenu}
                        >
                            <svg className="re-nav__hamburgerIcon" viewBox="0 0 24 24" aria-hidden="true">
                                <path className="re-line re-line1" d="M5 7H19" />
                                <path className="re-line re-line2" d="M5 12H19" />
                                <path className="re-line re-line3" d="M5 17H19" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="re-nav__hairline" aria-hidden="true" />
                <div className="re-nav__scrollmark" aria-hidden="true" />
            </nav>

            <div
                className={[
                    "re-drawer",
                    menuOpen ? "is-open" : "",
                    darkMode ? "re-drawer--dark" : "re-drawer--light",
                ].join(" ")}
                role="dialog"
                aria-modal="true"
                aria-hidden={!menuOpen}
                onClick={closeMenu}
            >
                <div className="re-drawer__panel" onClick={(e) => e.stopPropagation()}>
                    <div className="re-drawer__top">
                        <button
                            type="button"
                            className="re-drawer__brand"
                            aria-label="Inicio"
                            onClick={() => goTo(ROUTES.home)}
                        >
                            <img src={logoSrc} alt="Marca" className="re-drawer__logo" loading="eager" decoding="sync" />
                        </button>

                        <button
                            ref={closeRef}
                            type="button"
                            className="re-drawer__close"
                            aria-label="Cerrar menú"
                            onClick={closeMenu}
                        >
                            <svg className="re-drawer__closeIcon" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M6 6L18 18" />
                                <path d="M18 6L6 18" />
                            </svg>
                        </button>
                    </div>

                    <ul className="re-drawer__list" aria-label="Mobile primary">
                        {NAV_ITEMS.map((item, idx) => {
                            const current = isItemCurrent(item);
                            return (
                                <li
                                    key={`m-${item.label}`}
                                    className={`re-drawer__item ${current ? "is-current" : ""}`}
                                    style={{ ["--re-i"]: idx }}
                                >
                                    <button
                                        type="button"
                                        className="re-drawer__link"
                                        aria-current={current ? "page" : undefined}
                                        onClick={() => goTo(item.to)}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="re-drawer__bottom" aria-label="Apariencia">
                        <div className="re-drawer__toggleRow">
                            <div className="re-drawer__toggleText">
                                <div className="re-drawer__toggleTitle">Apariencia</div>
                                <div className="re-drawer__toggleSub">Modo claro u oscuro</div>
                            </div>
                            <div className="re-drawer__toggleAction">
                                <Suspense fallback={<span className="re-toggle-skeleton" aria-hidden="true" />}>
                                    <ThemeToggle />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
